import { Button } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { SignInPopupClosedError, signInWithGoogle } from '@/features/auth/signInWithGoogle';
import { Navigate, useNavigate, useSearchParams } from '@solidjs/router';
import { For, type JSX, Show, createEffect, createSignal, onCleanup, onMount } from 'solid-js';

const HERA_MESSENGER_ROUTES = [
  'M500 470C420 420 300 350 170 300',
  'M500 470C450 360 385 285 330 230',
  'M500 470C485 360 515 285 500 205',
  'M500 470C550 360 615 285 670 230',
  'M500 470C580 420 700 350 830 300',
  'M500 470C370 490 240 580 125 680',
  'M500 470C630 490 760 580 875 680',
] as const;

const randomBetween = (min: number, max: number) => Math.round(min + Math.random() * (max - min));

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { firebaseUser, session, refresh } = useAuth();
  const [pending, setPending] = createSignal(false);
  const [popupError, setPopupError] = createSignal<string | null>(null);
  const [reducedMotion, setReducedMotion] = createSignal(false);
  const messengerElements: SVGGElement[] = [];
  const flightTimers: number[] = [];
  let motif: SVGSVGElement | undefined;
  let messengersRunning = false;

  const scheduleMessenger = (element: SVGGElement, index: number, initial = false) => {
    const delay = initial ? randomBetween(200, 1500) : randomBetween(4000, 12000);
    flightTimers[index] = window.setTimeout(() => {
      if (!messengersRunning) return;
      const duration = randomBetween(4400, 7200);
      element.style.setProperty('--login-wing-speed', `${randomBetween(420, 720)}ms`);
      element.style.setProperty('--login-wing-delay', `${-randomBetween(0, 700)}ms`);
      for (const animation of element.querySelectorAll<SVGAnimationElement>(
        '.login-bird-animation',
      )) {
        animation.setAttribute('dur', `${duration}ms`);
        animation.beginElement?.();
      }
      flightTimers[index] = window.setTimeout(() => scheduleMessenger(element, index), duration);
    }, delay);
  };

  const startMessengers = () => {
    if (messengersRunning) return;
    messengersRunning = true;
    messengerElements.forEach((element, index) => scheduleMessenger(element, index, true));
  };

  const stopMessengers = () => {
    messengersRunning = false;
    flightTimers.forEach(clearTimeout);
  };

  createEffect(() => {
    if (pending() || reducedMotion()) {
      stopMessengers();
      motif?.pauseAnimations?.();
    } else {
      motif?.unpauseAnimations?.();
      startMessengers();
    }
  });

  onMount(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return;
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    onCleanup(() => media.removeEventListener('change', update));
  });

  onCleanup(stopMessengers);

  const onPointerMove: JSX.EventHandler<HTMLElement, PointerEvent> = (event) => {
    if (event.pointerType !== 'mouse') return;
    const page = event.currentTarget;
    const bounds = page.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    page.dataset.interacting = 'true';
    page.style.setProperty('--login-pointer-x', `${x}px`);
    page.style.setProperty('--login-pointer-y', `${y}px`);
    page.style.setProperty('--login-shift-x', `${(x / bounds.width - 0.5) * 10}px`);
    page.style.setProperty('--login-shift-y', `${(y / bounds.height - 0.5) * 10}px`);
    page.style.setProperty('--login-shift-soft-x', `${(x / bounds.width - 0.5) * 4}px`);
    page.style.setProperty('--login-shift-soft-y', `${(y / bounds.height - 0.5) * 4}px`);
  };

  const onPointerLeave: JSX.EventHandler<HTMLElement, PointerEvent> = (event) => {
    event.currentTarget.dataset.interacting = 'false';
    event.currentTarget.style.setProperty('--login-shift-x', '0px');
    event.currentTarget.style.setProperty('--login-shift-y', '0px');
    event.currentTarget.style.setProperty('--login-shift-soft-x', '0px');
    event.currentTarget.style.setProperty('--login-shift-soft-y', '0px');
  };

  const onClick = async () => {
    setPopupError(null);
    setPending(true);
    try {
      await signInWithGoogle();
      // signInWithGoogle force-refreshes the token; re-query /v1/auth/me.
      await refresh();
      navigate('/welcome');
    } catch (e) {
      if (e instanceof SignInPopupClosedError) {
        setPopupError('Sign-in popup closed. Please try again.');
      } else {
        setPopupError((e as Error).message || 'Sign-in failed. Please try again.');
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Show when={!session() || session() === null} fallback={<Navigate href="/welcome" />}>
      <main
        class="login-page relative isolate flex min-h-screen overflow-hidden px-6 py-12 sm:px-8"
        data-pending={pending()}
        data-interacting="false"
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
      >
        <svg
          ref={motif}
          class="login-motif"
          viewBox="0 0 1000 900"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="login-bird-wing-gold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#f1dfb7" />
              <stop offset="0.55" stop-color="#c8a968" />
              <stop offset="1" stop-color="#9b722d" />
            </linearGradient>
            <radialGradient id="login-bird-body-plum" cx="40%" cy="30%" r="75%">
              <stop offset="0" stop-color="#866593" />
              <stop offset="0.6" stop-color="#533b61" />
              <stop offset="1" stop-color="#35213f" />
            </radialGradient>
            <g id="login-team-voice">
              <path
                d="M-30-22H30A14 14 0 0 1 44-8V8A14 14 0 0 1 30 22H9L-5 34L-1 22H-30A14 14 0 0 1-44 8V-8A14 14 0 0 1-30-22Z"
                fill="#f7f2e8"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <circle cx="-20" cy="0" r="8" fill="currentColor" opacity="0.75" />
              <path d="M-5-6H24M-5 1H18M-5 8H10" stroke="currentColor" stroke-linecap="round" />
            </g>
            <g id="login-bird-wing-left-shape">
              <path
                d="M-6-4C-20-21-42-24-52-11C-37-8-25 3-11 15Z"
                fill="url(#login-bird-wing-gold)"
                stroke="#9b722d"
              />
              <path
                d="M-14 1C-25-8-37-12-46-11M-12 7C-24 1-34-1-42 0"
                stroke="#f7f2e8"
                opacity="0.7"
              />
            </g>
            <g id="login-bird-wing-right-shape">
              <path
                d="M6-4C20-21 42-24 52-11C37-8 25 3 11 15Z"
                fill="url(#login-bird-wing-gold)"
                stroke="#9b722d"
              />
              <path d="M14 1C25-8 37-12 46-11M12 7C24 1 34-1 42 0" stroke="#f7f2e8" opacity="0.7" />
            </g>
            <g id="login-bird-body-shape">
              <path d="M0 11C-8 29-27 44-35 55C-17 52-5 42 0 27Z" fill="#533b61" opacity="0.82" />
              <path d="M0 11C8 29 27 44 35 55C17 52 5 42 0 27Z" fill="#533b61" opacity="0.82" />
              <path d="M0 13C-7 35-2 50 0 59C2 50 7 35 0 13Z" fill="#b89552" />
              <path
                d="M-26 46L-19 43M26 46L19 43M0 49V42"
                stroke="#e9d39e"
                stroke-linecap="round"
              />
              <circle cx="-25" cy="46" r="4" fill="#b89552" stroke="#f1dfb7" />
              <circle cx="25" cy="46" r="4" fill="#b89552" stroke="#f1dfb7" />
              <circle cx="0" cy="49" r="4" fill="#533b61" stroke="#f1dfb7" />
              <ellipse
                cx="0"
                cy="5"
                rx="12"
                ry="23"
                fill="url(#login-bird-body-plum)"
                stroke="#35213f"
              />
              <path d="M-7 7Q0 15 7 7" stroke="#b89552" opacity="0.8" />
              <circle cx="0" cy="-16" r="10" fill="#b89552" stroke="#9b722d" />
              <path d="M-6-24L-9-35M0-26V-38M6-24L9-35" stroke="#9b722d" stroke-linecap="round" />
              <circle cx="-9" cy="-36" r="2" fill="#533b61" />
              <circle cx="0" cy="-39" r="2" fill="#533b61" />
              <circle cx="9" cy="-36" r="2" fill="#533b61" />
              <circle cx="-3.5" cy="-17" r="1.5" fill="#fff" />
              <circle cx="3.5" cy="-17" r="1.5" fill="#fff" />
              <path d="M-3-10L0-6L3-10Z" fill="#f7f2e8" />
              <rect x="-13" y="29" width="26" height="18" rx="5" fill="#fffdfa" stroke="#9b722d" />
              <path d="M-8 34L0 39L8 34" stroke="#533b61" stroke-linecap="round" />
            </g>
          </defs>

          <g class="login-motif-slow" fill="none" stroke="#b89552" stroke-width="1">
            <circle cx="500" cy="470" r="235" opacity="0.14" />
            <circle cx="500" cy="470" r="355" opacity="0.1" />
            <path d="M100 760Q500 500 900 760" opacity="0.2" />
            <path d="M135 710Q500 410 865 710" opacity="0.14" />
          </g>
          <g class="login-motif-fast" fill="none" stroke-linecap="round">
            <g stroke="#b89552" opacity="0.38">
              <path class="login-idle login-team-link" d="M500 470C430 370 300 330 170 300" />
              <path class="login-idle login-team-link" d="M500 470C445 335 390 270 330 230" />
              <path class="login-idle login-team-link" d="M500 470C500 340 500 260 500 205" />
              <path class="login-idle login-team-link" d="M500 470C555 335 610 270 670 230" />
              <path class="login-idle login-team-link" d="M500 470C570 370 700 330 830 300" />
            </g>
            <g stroke="#533b61" opacity="0.28">
              <path class="login-idle login-team-link" d="M500 470C375 485 245 565 125 680" />
              <path class="login-idle login-team-link" d="M500 470C625 485 755 565 875 680" />
            </g>

            <use
              class="login-idle login-team-voice"
              href="#login-team-voice"
              x="170"
              y="300"
              style="--login-voice-delay: -1s"
            />
            <use
              class="login-idle login-team-voice login-team-voice-gold"
              href="#login-team-voice"
              x="330"
              y="230"
              style="--login-voice-delay: -2s"
            />
            <use
              class="login-idle login-team-voice"
              href="#login-team-voice"
              x="500"
              y="205"
              style="--login-voice-delay: -3s"
            />
            <use
              class="login-idle login-team-voice login-team-voice-gold"
              href="#login-team-voice"
              x="670"
              y="230"
              style="--login-voice-delay: -4s"
            />
            <use
              class="login-idle login-team-voice"
              href="#login-team-voice"
              x="830"
              y="300"
              style="--login-voice-delay: -5s"
            />
            <use
              class="login-idle login-team-voice login-team-voice-gold"
              href="#login-team-voice"
              x="125"
              y="680"
              style="--login-voice-delay: -6s"
            />
            <use
              class="login-idle login-team-voice login-team-voice-gold"
              href="#login-team-voice"
              x="875"
              y="680"
              style="--login-voice-delay: -7s"
            />

            <For each={HERA_MESSENGER_ROUTES}>
              {(route) => (
                <g
                  ref={(element) => {
                    messengerElements.push(element);
                  }}
                  class="login-hera-messenger"
                  data-testid="hera-messenger"
                >
                  <animateMotion
                    class="login-bird-animation"
                    begin="indefinite"
                    dur="6s"
                    path={route}
                  />
                  <animate
                    class="login-bird-animation"
                    attributeName="opacity"
                    begin="indefinite"
                    dur="6s"
                    values="0;1;1;0"
                    keyTimes="0;0.12;0.82;1"
                  />
                  <g transform="scale(.68)">
                    <use
                      class="login-idle login-bird-wing-left"
                      href="#login-bird-wing-left-shape"
                    />
                    <use
                      class="login-idle login-bird-wing-right"
                      href="#login-bird-wing-right-shape"
                    />
                    <use href="#login-bird-body-shape" />
                  </g>
                </g>
              )}
            </For>
          </g>
        </svg>

        <section
          aria-labelledby="login-heading"
          class="login-card relative z-10 m-auto w-full max-w-[25rem] rounded-3xl border border-[#d9c9aa]/80 bg-white/90 px-6 py-9 text-center shadow-[0_20px_55px_rgba(24,32,51,0.12)] backdrop-blur-sm sm:px-10 sm:py-10"
        >
          <header>
            <h1
              aria-label="UChat"
              class="text-[2.75rem] font-semibold leading-none tracking-[-0.04em] text-[#182033]"
            >
              <span class="text-[#533b61]">U</span>Chat
            </h1>
            <div class="mt-3 flex items-center justify-center gap-3 text-sm font-medium text-[#9b722d]">
              <span class="h-px w-6 bg-[#b89552]" />
              Team Management
              <span class="h-px w-6 bg-[#b89552]" />
            </div>
          </header>

          <div class="mt-9">
            <h2 id="login-heading" class="text-2xl font-semibold tracking-tight text-[#182033]">
              Welcome back
            </h2>
            <p class="mt-2 text-sm text-[#667085]">Sign in with your Google account to continue.</p>
          </div>

          <div class="mt-7 space-y-3">
            <Show when={params.reason === 'expired'}>
              <p
                role="status"
                class="rounded-lg border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              >
                Your session expired. Please sign in again.
              </p>
            </Show>
            <Show when={popupError()}>
              <p
                role="alert"
                class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {popupError()}
              </p>
            </Show>

            <Button
              variant="ghost"
              size="lg"
              class="login-google-button w-full border border-[#747775] text-sm shadow-none disabled:opacity-70"
              onClick={onClick}
              disabled={pending()}
              aria-busy={pending()}
            >
              <Show
                when={!pending()}
                fallback={
                  <>
                    <span class="login-spinner" aria-hidden="true" />
                    Signing in…
                  </>
                }
              >
                <svg class="h-[18px] w-[18px]" viewBox="0 0 18 18" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.703-1.568 2.684-3.878 2.684-6.615Z"
                  />
                  <path
                    fill="#34A853"
                    d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.258c-.806.54-1.836.859-3.048.859-2.344 0-4.328-1.585-5.037-3.711H.956v2.333A9 9 0 0 0 9 18Z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M3.963 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.281-1.71V4.957H.956A9 9 0 0 0 0 9c0 1.452.348 2.827.956 4.043l3.007-2.333Z"
                  />
                  <path
                    fill="#EA4335"
                    d="M9 3.579c1.321 0 2.508.455 3.442 1.346l2.581-2.581C13.464.89 11.426 0 9 0A9 9 0 0 0 .956 4.957L3.963 7.29C4.672 5.164 6.656 3.579 9 3.579Z"
                  />
                </svg>
                Continue with Google
              </Show>
            </Button>
            <Button
              variant="ghost"
              size="lg"
              class="login-passkey-button w-full text-sm shadow-none"
              disabled
              aria-label="Login with passkey — Coming soon"
            >
              <svg
                class="h-[18px] w-[18px] flex-none"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="7.5" cy="7" r="3" stroke="currentColor" stroke-width="1.7" />
                <path
                  d="M2.5 18C3.1 14.8 5 13 7.5 13C9.2 13 10.6 13.8 11.5 15"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
                <circle cx="15.5" cy="10.5" r="2.5" stroke="currentColor" stroke-width="1.7" />
                <path
                  d="M18 10.5H22M20 10.5V13M18.5 10.5V12"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
              </svg>
              <span>Login with passkey</span>
              <span class="login-coming-soon" aria-hidden="true">
                Coming soon
              </span>
            </Button>
            <Show when={pending()}>
              <span class="sr-only" role="status">
                Sign-in in progress.
              </span>
            </Show>

            <Show when={firebaseUser()}>
              <p class="text-xs text-[#667085]">Signed in as {firebaseUser()?.email}</p>
            </Show>
          </div>
        </section>
      </main>
    </Show>
  );
}
